import { Link } from 'react-router-dom';

export function ClientPage() {
    const downloadOptions = [
        {
            title: 'Self-contained download',
            subtitle: 'Recommended for most users',
            description:
                'Includes the required .NET runtime pieces in the package. Larger download, simpler setup.',
            href: '/downloads/fzClient-self-contained.zip',
            cta: 'Download self-contained ZIP',
            meta: 'No separate .NET install required'
        },
        {
            title: 'Framework-dependent download',
            subtitle: 'Smaller download',
            description:
                'Use this if you already have the required .NET 10 Runtime installed.',
            href: '/downloads/fzClient-framework-dependent.zip',
            cta: 'Download framework-dependent ZIP',
            meta: 'Requires .NET 10  Desktop Runtime'
        }
    ];

    const features = [
        {
            title: 'Auto Update Bonuses',
            text: 'Submit zone bonuses to the site automatically, from the client.'
        },
        {
            title: 'Live log parsing',
            text: 'Watch an active EverQuest log file and build encounter-by-encounter analyses as they happen.'
        },
        {
            title: 'Replay parsing',
            text: 'Load existing logs to review past sessions and get detailed breakdowns.'
        },
        {
            title: 'Damage and healing meters',
            text: 'See real-time DPS/HPS meters anywhere on your screen.'
        },
        {
            title: 'Report uploads',
            text: 'Upload live logs into Frostreaver.zone / Fangbreakzer.zone for rankings, guild history, and analysis.'
        },
        {
            title: 'Pet attribution',
            text: 'Map pets to owners so in-client analytics and uploaded reports work for pet classes.'
        },
        {
            title: 'Triggers & Actions',
            text: 'Setup log triggers and add as many actions as you need. Text, Sounds Effects, Timers, Text-to-Speech are all included. Regular Expressions are supported. Set offset times on actions. Configure and style trigger action windows to your liking. This functionality is an alpha-test WIP. I recommend running NAG in tandem, but I greatly appreciate any feedback you can offer..'
        },
        {
            title: 'Configure Profiles and Groups',
            text: 'Setup character-specific profiles and/or group triggers->actions into arbitrary groups for easy swapping and/or boxing.'
        }
    ];

    const setupSteps = [
        'Download the version that fits your setup.',
        'Extract the ZIP wherever you want it.',
        'Open fzClient and select your EverQuest log file.',
        'Open the Connections tab and enter your API key (Create one in your account settings here!), and if you want, a guild upload code when you are ready to upload.',
        'Enable Upload Live Logs and start a Live Log sessions to stream combat data to Frostreaver.zone / Fangbreaker.zone in real-time.'
    ];

    return (
        <>
            <section className="page-hero">
                <div className="content-wrapper py-4">
                    <h1 className="display-6 fw-semibold mb-0">fzClient</h1>
                    <span className="page-hero__server d-block">Log Parser and Uploader</span>
                </div>
            </section>

            <div className="content-wrapper py-4">
                <div className="row g-4 align-items-stretch mb-4">
                    <div className="col-12 col-xl-7">
                        <div className="card h-100 rounded-0">
                            <div className="card-body p-4 p-lg-5">
                                <p className="text-uppercase small text-secondary fw-semibold mb-2">Desktop companion app</p>
                                <h2 className="h3 mb-3">Push Zone Bonus Updates Automatically.</h2>
                                <p className="lead mb-4">Upload any detected zone bonuses with your combat data, or as a stand-alone update.</p>
                                <h2 className="h3 mb-3">Parse logs, review encounters, and upload reports.</h2>
                                <p className="lead mb-4">
                                    fzClient is the Windows desktop client for Frostreaver.zone. It reads EverQuest log
                                    files, builds damage and healing summaries, powers local meters and overlays, and uploads
                                    finalized encounters into browsable raid reports.
                                </p>
                                <h2 className="h2 mb-3">fzClient is going to be reworked in a big way.</h2>
                                <p className='lead mb-4'>I'm beyond thrilled with performance while replaying a log's history. Live-logging, on the other hand, has far too much overhead for my liking. It works - and in some ways (the responsiveness of the overlays) I think it's better than other options. But honestly? There's too much overhead and memory usage creeps too high for comfort. It's not a leak - it's just a byproduct of the snapshot based architecture I chose to go with. Please be aware that in its current state, restarting the client every few hours is almost necessary. Check back for some updates later!</p>
                                

                                <div className="row g-3">
                                    {downloadOptions.map((option) => (
                                        <div className="col-12 col-lg-6" key={option.href}>
                                            <div className="border h-100 p-3 bg-body-tertiary">
                                                <p className="text-uppercase small text-secondary fw-semibold mb-1">{option.subtitle}</p>
                                                <h3 className="h5 mb-2">{option.title}</h3>
                                                <p className="small text-secondary mb-3">{option.description}</p>
                                                <a className="btn btn-primary w-100 mb-2" href={option.href}>
                                                    {option.cta}
                                                </a>
                                                <p className="small text-secondary mb-0">{option.meta}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xl-5">
                        <div className="card h-100 rounded-0">
                            <div className="card-body p-4">
                                <div className="ratio ratio-16x9 border bg-black bg-opacity-25 d-flex align-items-center justify-content-center mb-3">
                                    <img src='/replay.webp' alt='fzClient UI' />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row g-4 mb-4">
                    <div className="col-12 col-xl-8">
                        <div className="card rounded-0 h-100">
                            <div className="card-body p-4">
                                <h2 className="h5 mb-3">What it does</h2>
                                <div className="row g-3">
                                    {features.map((feature) => (
                                        <div className="col-12 col-md-6" key={feature.title}>
                                            <div className="border h-100 p-3">
                                                <h3 className="h6 mb-2">{feature.title}</h3>
                                                <p className="small text-secondary mb-0">{feature.text}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xl-4">
                        <div className="card rounded-0 h-100">
                            <div className="card-body p-4">
                                <h2 className="h5 mb-3">Quick setup</h2>
                                <ol className="ps-3 mb-3">
                                    {setupSteps.map((step) => (
                                        <li className="mb-2" key={step}>{step}</li>
                                    ))}
                                </ol>
                                <h2 className='h5 mb-3'>Notes:</h2>
                                <p>Windows is super hand-holdy about unsigned code now. It is what it is. The exe is portable and settings are stored in AppData, so it doesn't require admin or any UAC permissions. I did <em>not</em> get a Smart App Control freakout on the only computer I have without it disabled. I can't guarantee the same.</p>
                                <p>To ensure integrity with logs that are not especially detailed and easily spoofed, logs can only be uploaded by streaming them in real-time.</p>
                                <p>While I've put a lot of effort into making sure everything one would want does work, I understand that a more mature and proven solution may be preferable for raid triggers. You can run this and EQLP in tandem if you prefer their trigger functionality but still want to live-log for parse ranks.</p>
                            </div>
                        </div>
                    </div>
                </div>

                
            </div>
        </>
    );
}
